// src/pages/settings/ManageBrands.js
import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import API_BASE_URL from "../../config";
import { Link } from "react-router-dom";

const ManageBrands = () => {
  const [brands, setBrands] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Manage Brands | Webyappy";
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}/invoicing/get_brands`, {
      credentials: "include",
    });
    const data = await res.json();

    if (data.status === "success") {
      const enriched = data.brands.map((b) => ({
        ...b,
        logoFile: null,
        logo: b.logo
          ? b.logo.startsWith("http")
            ? b.logo
            : `${API_BASE_URL.replace("/index.php", "")}/${b.logo}`
          : "",
      }));
      setBrands(enriched);
    }
  } catch (err) {
    console.error("Error fetching brands:", err);
  }
};

  const handleInputChange = (index, field, value) => {
    const updated = [...brands];
    updated[index][field] = value;
    setBrands(updated);
  };

  const handleFileChange = (index, file) => {
    const updated = [...brands];
    updated[index].logoFile = file;
    updated[index].logo = URL.createObjectURL(file); // preview
    setBrands(updated);
  };

  const addBrand = () => {
    setBrands([
      ...brands,
      { brand_name: "", color_code: "#000000", logo: "", logoFile: null },
    ]);
  };

  const removeBrand = async (index) => {
  const brand = brands[index];
  console.log("Attempting to delete brand:", brand);

  if (brand.id) {
    const confirmDelete = window.confirm("Are you sure you want to delete this brand?");
    if (!confirmDelete) return;

    try {
      const res = await fetch(`${API_BASE_URL}/invoicing/delete_brand/${brand.id}`, {
        method: "DELETE",
        credentials: "include",
      });

      const result = await res.json(); // ✅ parse response
      console.log("Delete API result:", result);

      if (result.status === "success") {
        const updated = brands.filter((_, i) => i !== index);
        setBrands(updated);
      } else {
        alert("Failed to delete brand");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Delete request failed");
    }
  } else {
    // Newly added brand not yet saved to DB
    const updated = brands.filter((_, i) => i !== index);
    setBrands(updated);
  }
};


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const formData = new FormData();
    brands.forEach((brand, index) => {
      formData.append(`brands[${index}][id]`, brand.id || "");
      formData.append(`brands[${index}][brand_name]`, brand.brand_name);
      formData.append(`brands[${index}][color_code]`, brand.color_code);
      if (brand.logoFile) {
        formData.append(`brands[${index}][logo]`, brand.logoFile);
      }
    });

    try {
      const res = await fetch(`${API_BASE_URL}/invoicing/save_brands`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      const data = await res.json();
      if (data.status === "success") {
        alert("Brands saved successfully!");
        fetchBrands();
      } else {
        alert("Failed to save brands");
      }
    } catch (err) {
      console.error("Error saving brands:", err);
      alert("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <div style={{ paddingTop: "70px", paddingBottom: "70px" }}>
        <div className="container-fluid px-3 py-3 col-lg-9 mx-auto">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <ul className="breadcrumb breadcrumb-transparent breadcrumb-dot font-weight-bold mb-0 p-0">
              <li className="breadcrumb-item">
                <Link to="/home" className="text-muted">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/pages/settings/Settings" className="text-muted">
                  Settings
                </Link>
              </li>
              <li className="breadcrumb-item">
                <span className="text-dark font-weight-bold">
                  Manage Brands
                </span>
              </li>
            </ul>
          </div>

          <div className="card card-custom shadow-sm mb-5">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4 border-bottom p-2">
                <h5 className="font-weight-boldest mb-0">Manage Brands</h5>
                <Link to="/pages/settings/Settings" className="btn btn-light">
                  <i className="fas fa-arrow-left fa-xs mr-1"></i> Back
                </Link>
              </div>
              <form onSubmit={handleSubmit}>
                {brands.map((brand, index) => (
                  <div
                    key={index}
                    className="border rounded p-4 mb-4 position-relative shadow-sm"
                  >
                    <button
                      type="button"
                      className="btn btn-sm btn-icon btn-danger position-absolute"
                      style={{ top: 15, right: 15 }}
                      onClick={() => removeBrand(index)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>

                    <h6 className="font-weight-bold mb-3">
                      Brand #{index + 1}
                    </h6>

                    <div className="row">
                      <div className="form-group col-lg-4 mb-3">
                        <label className="small">Brand Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={brand.brand_name}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "brand_name",
                              e.target.value,
                            )
                          }
                        />
                      </div>

                      <div className="form-group col-lg-4 mb-3">
                        <label className="small">Brand Color (Hex Code)</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="#f26722"
                          maxLength={7}
                          value={brand.color_code}
                          onChange={(e) =>
                            handleInputChange(
                              index,
                              "color_code",
                              e.target.value,
                            )
                          }
                        />
                      </div>

                      <div className="form-group col-lg-4 mb-3">
                        <label className="small">Logo (PNG, max 1MB)</label>
                        <input
                          type="file"
                          className="form-control-file"
                          accept="image/png"
                          onChange={(e) =>
                            handleFileChange(index, e.target.files[0])
                          }
                        />
                        {brand.logo && (
                          <img
                            src={brand.logo}
                            alt="Preview"
                            height={50}
                            className="mt-2 border d-block"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <div className="text-right mb-4">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary font-weight-bold"
                    onClick={addBrand}
                  >
                    + Add Brand
                  </button>
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    className="btn btn-primary font-weight-bold px-5 w-100"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageBrands;
